import { useHabits } from "@/context/SupabaseHabitContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Pin, ExternalLink } from "lucide-react";
import { useIsMobile } from "@/hooks/use-mobile";
import CompactHabitGrid from "./CompactHabitGrid";

export default function PinnedHabits() {
  const { pinnedHabits } = useHabits();
  const isMobile = useIsMobile();

  // Function to scroll to the full habit container
  const scrollToHabit = (habitId: string) => {
    const element = document.getElementById(`habit-${habitId}`);
    if (element) {
      element.scrollIntoView({ 
        behavior: 'smooth', 
        block: 'start',
        inline: 'nearest'
      });
      
      // Add a subtle highlight effect
      element.classList.add('ring-2', 'ring-primary', 'ring-opacity-50');
      setTimeout(() => {
        element.classList.remove('ring-2', 'ring-primary', 'ring-opacity-50');
      }, 2000);
    }
  };

  // Show helpful message when no pinned habits
  if (pinnedHabits.length === 0) {
    return (
      <div className="mb-8 text-center py-3">
        <p className="text-sm text-muted-foreground">
          No pinned habits yet. 
          <span className="inline-flex items-center gap-1 mx-1">
            Try pinning <Pin className="h-3 w-3" /> your most important habits!
          </span>
        </p>
      </div>
    );
  }

  // Determine grid layout based on screen size and number of habits
  const getGridClasses = () => {
    if (isMobile) {
      // On mobile: 1 column for 1 habit, 2 columns for 2+ habits
      return pinnedHabits.length === 1 ? "grid grid-cols-1 gap-4" : "grid grid-cols-2 gap-3";
    } else {
      // On desktop: responsive based on number of habits
      if (pinnedHabits.length === 1) return "grid grid-cols-1 gap-4 max-w-sm";
      if (pinnedHabits.length === 2) return "grid grid-cols-2 gap-4";
      if (pinnedHabits.length === 3) return "grid grid-cols-3 gap-4";
      return "grid grid-cols-4 gap-4";
    }
  };

  return (
    <Card className="mb-8">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Pin className="text-primary h-5 w-5" />
            <CardTitle className="text-xl">Pinned Habits</CardTitle>
          </div>
          <span className="text-sm text-muted-foreground bg-muted px-2 py-1 rounded-full">
            {pinnedHabits.length}/4
          </span>
        </div>
      </CardHeader>
      <CardContent className="pt-0">
        <div className={getGridClasses()}>
          {pinnedHabits.map((habit) => (
            <div 
              key={habit.id} 
              className="space-y-3 min-w-0 p-3 rounded-lg border bg-card/50 cursor-pointer hover:bg-card/70 transition-colors group overflow-hidden"
              onClick={() => scrollToHabit(habit.id)}
              title="Click to scroll to full habit"
            >
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-2 min-w-0">
                  <div 
                    className="w-3 h-3 rounded-sm flex-shrink-0" 
                    style={{ backgroundColor: habit.color.filled }}
                  />
                  <h4 className="text-sm font-medium truncate group-hover:text-primary transition-colors">
                    {habit.name}
                  </h4>
                </div>
                <ExternalLink className="h-3 w-3 text-muted-foreground group-hover:text-primary transition-colors opacity-0 group-hover:opacity-100 flex-shrink-0" />
              </div>
              <div className="overflow-hidden w-full">
                <CompactHabitGrid habit={habit} />
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
} 